import type { ShirtCut, Size } from '../types';

type Measurement = { widthCm: number; lengthCm: number };

// Garment measurements from the blank maker's spec sheets: JHK TSRA 190 (męski) and
// TSRL PRM (damski). Both colours are the same blank, so the chart is per cut, not per product.
const SIZE_CHART: Record<ShirtCut, Partial<Record<Size, Measurement>>> = {
  męski: {
    XS: { widthCm: 48, lengthCm: 68 },
    S: { widthCm: 51, lengthCm: 70 },
    M: { widthCm: 53, lengthCm: 72 },
    L: { widthCm: 56, lengthCm: 74 },
    XL: { widthCm: 58, lengthCm: 76 },
    XXL: { widthCm: 61, lengthCm: 78 },
    '3XL': { widthCm: 66, lengthCm: 82 },
    '4XL': { widthCm: 68, lengthCm: 84 },
    '5XL': { widthCm: 72, lengthCm: 88 },
  },
  damski: {
    S: { widthCm: 42, lengthCm: 62 },
    M: { widthCm: 44, lengthCm: 63 },
    L: { widthCm: 46, lengthCm: 64 },
    XL: { widthCm: 48, lengthCm: 66 },
    XXL: { widthCm: 50, lengthCm: 68 },
    '3XL': { widthCm: 52, lengthCm: 70 },
  },
};

/** Measurements of the sizes on sale in this cut; a size missing from the chart is left out. */
export function SizeChart({
  cut,
  sizes,
  selected,
  fontSize = 12,
}: {
  cut: ShirtCut;
  sizes: Size[];
  selected: Size;
  fontSize?: number;
}) {
  const columns = sizes.flatMap((size) => {
    const m = SIZE_CHART[cut][size];
    return m ? [{ size, ...m }] : [];
  });
  if (columns.length === 0) return null;

  const muted = 'rgba(var(--shop-muted-rgb),.6)';
  const cell = (active: boolean) =>
    ({
      padding: '5px 0',
      textAlign: 'center',
      background: active ? 'var(--orange)' : 'transparent',
      color: active ? 'var(--ink)' : 'var(--shop-fg)',
    }) as const;
  const rowLabel = {
    padding: '5px 8px 5px 0',
    textAlign: 'left',
    fontWeight: 'normal',
    color: muted,
    whiteSpace: 'nowrap',
  } as const;

  return (
    <div>
      <table
        className="mono"
        aria-label={`Tabela wymiarów, krój ${cut}`}
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          fontSize,
          letterSpacing: '.04em',
        }}
      >
        <colgroup>
          <col style={{ width: '6.5em' }} />
          {columns.map((c) => (
            <col key={c.size} />
          ))}
        </colgroup>
        <thead>
          <tr style={{ borderBottom: '1px dashed rgba(var(--shop-muted-rgb),.3)' }}>
            <th scope="row" style={rowLabel}>
              ROZMIAR
            </th>
            {columns.map((c) => (
              <th key={c.size} scope="col" style={{ ...cell(c.size === selected), fontWeight: 'bold' }}>
                {c.size}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row" style={rowLabel}>
              SZEROKOŚĆ
            </th>
            {columns.map((c) => (
              <td key={c.size} style={cell(c.size === selected)}>
                {c.widthCm}
              </td>
            ))}
          </tr>
          <tr>
            <th scope="row" style={rowLabel}>
              DŁUGOŚĆ
            </th>
            {columns.map((c) => (
              <td key={c.size} style={cell(c.size === selected)}>
                {c.lengthCm}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
      <div
        className="mono"
        style={{ marginTop: 6, fontSize: fontSize - 2, color: muted, letterSpacing: '.08em', lineHeight: 1.5 }}
      >
        WYMIARY KOSZULKI W CM, MIERZONE NA PŁASKO: SZEROKOŚĆ POD PACHAMI, DŁUGOŚĆ OD RAMIENIA DO DOŁU.
        TOLERANCJA DO ±5%.
      </div>
    </div>
  );
}
